import { useEffect, useState } from "react";
import { Clock, Flame } from "lucide-react";

function diffParts(end) {
    const now = Date.now();
    const ms = end - now;
    if (ms <= 0) return { ended: true, d: 0, h: 0, m: 0, s: 0, ms: 0 };
    const total = Math.floor(ms / 1000);
    const d = Math.floor(total / 86400);
    const h = Math.floor((total % 86400) / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    return { ended: false, d, h, m, s, ms };
}

export default function AuctionTimer({ endIso, compact = false }) {
    const [parts, setParts] = useState(() => {
        if (!endIso) return null;
        return diffParts(new Date(endIso).getTime());
    });

    useEffect(() => {
        if (!endIso) return undefined;
        const end = new Date(endIso).getTime();
        const tick = () => setParts(diffParts(end));
        tick();
        const id = setInterval(tick, 1000);
        return () => clearInterval(id);
    }, [endIso]);

    if (!parts) return null;

    if (parts.ended) {
        return (
            <span className={`inline-flex items-center gap-1.5 font-bold uppercase tracking-wider ${compact ? "text-xs" : "text-sm"} text-red-600`} data-testid="auction-ended">
                <Clock className="w-3.5 h-3.5" /> Beendet
            </span>
        );
    }

    const urgent = parts.d === 0;
    const cls = urgent ? "text-red-600" : "text-[#111827]";
    const Icon = urgent ? Flame : Clock;

    if (compact) {
        const summary = parts.d > 0
            ? `${parts.d}T ${parts.h}h`
            : parts.h > 0
            ? `${parts.h}h ${String(parts.m).padStart(2, "0")}m`
            : `${parts.m}m ${String(parts.s).padStart(2, "0")}s`;
        return (
            <span className={`inline-flex items-center gap-1.5 font-bold font-mono-num text-xs ${cls} ${urgent ? "animate-pulse" : ""}`} data-testid="auction-timer-compact">
                <Icon className="w-3.5 h-3.5" /> {summary}
            </span>
        );
    }

    const segments = [
        { v: parts.d, l: "Tage" },
        { v: parts.h, l: "Std" },
        { v: parts.m, l: "Min" },
        { v: parts.s, l: "Sek" },
    ];
    return (
        <div className={`flex items-center gap-2 ${cls} ${urgent ? "animate-pulse" : ""}`} data-testid="auction-timer">
            <Icon className="w-4 h-4" />
            <div className="flex gap-1.5">
                {segments.map((s) => (
                    <div key={s.l} className="bg-[#111827] text-[#FFCC00] px-2 py-1 rounded-sm min-w-[36px] text-center">
                        <div className="font-display font-black text-lg font-mono-num leading-none">{String(s.v).padStart(2, "0")}</div>
                        <div className="text-[9px] uppercase tracking-wider text-white/70 mt-0.5">{s.l}</div>
                    </div>
                ))}
            </div>
        </div>
    );
}
